import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { useAuth } from "@/lib/auth";
import { fetchInitData, type PayReqInitData } from "@/lib/payreq";

interface InitDataState extends PayReqInitData {
  loaded: boolean;
  /** Re-fetch (the form calls this when the first load failed). */
  reload: () => void;
}

const EMPTY: Omit<InitDataState, "reload"> = {
  priorities: [],
  modeOfPayments: [],
  expensesTypes: [],
  myDepartments: [],
  legalEntities: [],
  myOperationUnits: [],
  banks: [],
  budgetYearId: null,
  reqTypePaymentRequestId: null,
  reqTypeInternalTransferId: null,
  canMakeRegularRequest: false,
  canMakeInternalTransfer: false,
  canApproveRequests: false,
  bankModeOfPaymentIds: [],
  loaded: false,
};
const InitDataContext = createContext<InitDataState>({ ...EMPTY, reload: () => {} });

/**
 * Loads /api/myPaymentRequest/apiInitData once per session (after sign-in) and keeps it
 * available for the request form (priorities, modes of payment, legal entities, my departments,
 * my operation units).
 */
export function InitDataProvider({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth();
  const [state, setState] = useState<Omit<InitDataState, "reload">>(EMPTY);
  const [reloadSeq, setReloadSeq] = useState(0);

  useEffect(() => {
    if (!ready || !token) {
      setState(EMPTY);
      return;
    }
    const controller = new AbortController();
    fetchInitData(token, controller.signal)
      .then((d) => {
        setState({
          priorities: d?.priorities ?? [],
          modeOfPayments: d?.modeOfPayments ?? [],
          expensesTypes: d?.expensesTypes ?? [],
          myDepartments: d?.myDepartments ?? [],
          legalEntities: d?.legalEntities ?? [],
          myOperationUnits: d?.myOperationUnits ?? [],
          banks: d?.banks ?? [],
          budgetYearId: d?.budgetYearId ?? null,
          reqTypePaymentRequestId: d?.reqTypePaymentRequestId ?? null,
          reqTypeInternalTransferId: d?.reqTypeInternalTransferId ?? null,
          canMakeRegularRequest: d?.canMakeRegularRequest ?? false,
          canMakeInternalTransfer: d?.canMakeInternalTransfer ?? false,
          canApproveRequests: d?.canApproveRequests ?? false,
          // Coerce defensively — mode-of-payment option ids elsewhere are numbers.
          bankModeOfPaymentIds: (d?.bankModeOfPaymentIds ?? []).map(String),
          loaded: true,
        });
      })
      .catch(() => {
        // Non-fatal: the list/detail screens don't need it. The form calls reload() to retry.
      });
    return () => controller.abort();
  }, [ready, token, reloadSeq]);

  const reload = useCallback(() => setReloadSeq((s) => s + 1), []);

  return <InitDataContext.Provider value={{ ...state, reload }}>{children}</InitDataContext.Provider>;
}

export function useInitData() {
  return useContext(InitDataContext);
}
